import { Images, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  listSpriteLibraryItems,
  SPRITE_LIBRARY_GROUP_LABELS,
  type SpriteLibraryGroup,
  type SpriteLibraryItem,
} from "@/lib/office/sprites/labels";
import { cn } from "@/lib/utils";

const GROUPS = Object.keys(SPRITE_LIBRARY_GROUP_LABELS) as SpriteLibraryGroup[];

type Props = {
  className?: string;
  /** Compact tray mode (ready-room right column) vs full stage. */
  layout?: "tray" | "stage";
};

/**
 * Admin-only furniture sprite browser for the ready room.
 * Read-only inventory of catalog kinds + thumbnails (map editor still places them).
 */
export function SpriteLibraryPanel({ className, layout = "tray" }: Props) {
  const all = useMemo(() => listSpriteLibraryItems(), []);
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState<SpriteLibraryGroup | "all">("all");
  const [selected, setSelected] = useState<SpriteLibraryItem | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter((item) => {
      if (group !== "all" && item.group !== group) return false;
      if (!q) return true;
      return (
        item.kind.includes(q) ||
        item.label.toLowerCase().includes(q) ||
        SPRITE_LIBRARY_GROUP_LABELS[item.group].toLowerCase().includes(q)
      );
    });
  }, [all, group, query]);

  const byGroup = useMemo(() => {
    const map = new Map<SpriteLibraryGroup, SpriteLibraryItem[]>();
    for (const item of filtered) {
      const list = map.get(item.group) ?? [];
      list.push(item);
      map.set(item.group, list);
    }
    return map;
  }, [filtered]);

  return (
    <div className={cn("flex min-h-0 flex-col gap-3", className)}>
      <div className="flex items-start gap-2">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Images className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">Sprite library</p>
          <p className="text-xs text-muted-foreground">
            Admin inventory of HQ furniture art. Place items in Map layout.
          </p>
        </div>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search sprites…"
          className="h-9 pl-8 text-sm"
          aria-label="Search sprites"
        />
      </div>

      <div className="flex flex-wrap gap-1">
        <FilterChip active={group === "all"} onClick={() => setGroup("all")} label="All" />
        {GROUPS.map((g) => (
          <FilterChip
            key={g}
            active={group === g}
            onClick={() => setGroup(g)}
            label={SPRITE_LIBRARY_GROUP_LABELS[g]}
          />
        ))}
      </div>

      <p className="text-[11px] text-muted-foreground">
        {filtered.length} of {all.length} sprites
        {selected ? ` · selected ${selected.label}` : ""}
      </p>

      <div
        className={cn(
          "min-h-0 flex-1 overflow-y-auto overscroll-contain rounded-lg border border-border/70 bg-muted/20",
          layout === "stage" ? "p-3" : "p-2",
        )}
      >
        {filtered.length === 0 ? (
          <p className="p-4 text-center text-sm text-muted-foreground">No sprites match.</p>
        ) : group === "all" ? (
          GROUPS.map((g) => {
            const items = byGroup.get(g);
            if (!items?.length) return null;
            return (
              <div key={g} className="mb-4 last:mb-0">
                <h3 className="mb-2 px-1 text-[11px] font-bold tracking-wide text-muted-foreground uppercase">
                  {SPRITE_LIBRARY_GROUP_LABELS[g]}
                </h3>
                <SpriteGrid
                  items={items}
                  selectedKind={selected?.kind ?? null}
                  onSelect={setSelected}
                  large={layout === "stage"}
                />
              </div>
            );
          })
        ) : (
          <SpriteGrid
            items={filtered}
            selectedKind={selected?.kind ?? null}
            onSelect={setSelected}
            large={layout === "stage"}
          />
        )}
      </div>

      {selected ? (
        <div className="shrink-0 rounded-lg border border-border bg-card p-3">
          <div className="flex items-center gap-3">
            <SpriteThumb item={selected} size={layout === "stage" ? 72 : 56} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{selected.label}</p>
              <p className="font-mono text-[11px] text-muted-foreground">{selected.kind}</p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {selected.footprint.w}×{selected.footprint.d}
                {selected.seat ? " · seat" : ""}
                {selected.block ? " · blocks walk" : ""}
                {selected.useSprite ? " · sprite draw" : " · procedural draw"}
              </p>
            </div>
          </div>
          {selected.assetSrc ? (
            <p className="mt-2 truncate font-mono text-[10px] text-muted-foreground">
              {selected.assetSrc}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-colors",
        active
          ? "border-primary bg-primary/10 text-foreground"
          : "border-border text-muted-foreground hover:bg-muted",
      )}
    >
      {label}
    </button>
  );
}

function SpriteGrid({
  items,
  selectedKind,
  onSelect,
  large,
}: {
  items: SpriteLibraryItem[];
  selectedKind: string | null;
  onSelect: (item: SpriteLibraryItem) => void;
  large?: boolean;
}) {
  return (
    <div
      className={cn(
        "grid gap-1.5",
        large ? "grid-cols-3 sm:grid-cols-4 md:grid-cols-5" : "grid-cols-3 sm:grid-cols-4",
      )}
    >
      {items.map((item) => {
        const active = selectedKind === item.kind;
        return (
          <button
            key={item.kind}
            type="button"
            onClick={() => onSelect(item)}
            className={cn(
              "flex flex-col items-center gap-1 rounded-lg border p-1.5 text-center transition-colors",
              active
                ? "border-primary bg-primary/10"
                : "border-border/60 bg-background/80 hover:border-primary/40 hover:bg-muted/50",
            )}
            title={item.label}
          >
            <SpriteThumb item={item} size={large ? 56 : 44} />
            <span className="line-clamp-2 w-full text-[10px] leading-tight font-medium text-foreground">
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SpriteThumb({ item, size }: { item: SpriteLibraryItem; size: number }) {
  const src = item.thumbSrc ?? item.assetSrc;
  if (!src) {
    return (
      <span
        className="flex items-center justify-center rounded-md bg-muted font-mono text-[10px] font-bold text-muted-foreground"
        style={{ width: size, height: size }}
      >
        {item.kind.slice(0, 2).toUpperCase()}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      className="rounded-md border border-border/50 bg-[var(--nymbl-cloud,#E6EBF2)] object-contain"
      style={{ width: size, height: size, imageRendering: "pixelated" }}
      loading="lazy"
      draggable={false}
    />
  );
}
